import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DossierMember } from '../../models/DossierMember';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { ConfirmModal } from '../../features/confirm-modal/ConfirmModal';
import PersonSelector from '../appointments/PersonSelector';
import Alert from 'react-bootstrap/esm/Alert';
import { ISessionAppState } from '../../app/session/reducer';
import { Api } from '../../api';
import { NotificationHelper } from '../../helpers/NotificationHelper';

export interface IAddPersonToGroupFormProps {
  groupId: number;
  currentMembers: DossierMember[];
  onAdded: (person: DossierMember) => void;
  onCancel: () => void;
}

export function AddPersonToGroupForm({ groupId, onAdded, onCancel, currentMembers }: IAddPersonToGroupFormProps): JSX.Element {
  const session = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const possibleDossierRelations = useMemo<DossierMember[]>(() => (
    (session.dossierRelations || [])
      .filter(relation => !currentMembers.some(currentMember => currentMember.memberId === relation.memberId))
      .map(relation => new DossierMember(relation))
  ), [session.dossierRelations, currentMembers]);
  const [newMember, setNewMember] = useState<DossierMember|undefined>(undefined);
  const [newMemberError, setNewMemberError] = useState<string|undefined >(undefined);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const isValid = useMemo<boolean>(() => !newMemberError, [newMemberError]);

  const validate = useCallback((): boolean => {
    setNewMemberError(undefined);

    let isValid = true;
    if (!newMember) {
      setNewMemberError('Selecteer een persoon');
      isValid = false;
    }

    return isValid;
  }, [newMember]);

  useEffect(() => {
    if (!isValid) {
      validate();
    }
  }, [newMember, isValid, validate]);

  const clear = (): void => {
    setNewMember(undefined);
    setNewMemberError(undefined);
  };

  const handleCancel = (): void => {
    clear();
    onCancel();
  };

  const handleSubmit = (): void => {
    if (!validate() || !groupId || !newMember) {
      return;
    }
    setSubmitting(true);

    Api.addRelationToChatGroup(groupId, newMember)
      .then(() => {
        clear();
        onAdded(newMember);
      })
      .catch(err => {
        console.error(err);
        NotificationHelper.toastError('Toevoegen aan groep mislukt door een onverwachte fout');
      })
      .finally(() => setSubmitting(false));
  };

  const handleSelectMember = (selectedMember: DossierMember): void => {
    setNewMember(selectedMember);
  };

  const renderBody = () => <>
    <PersonSelector persons={possibleDossierRelations} selected={newMember ? [newMember] : []} onClickItem={handleSelectMember} />
    { newMemberError && <Alert className="add-person-to-group-form__danger" variant="danger">{newMemberError}</Alert> }
  </>;

  return <ConfirmModal
    title="Persoon toevoegen aan groep"
    body={renderBody()}
    confirmLabel="Toevoegen"
    confirmButtonStyle="default"
    onCancel={handleCancel}
    onConfirm={handleSubmit}
    confirming={submitting}
  />;
}
